// frontend/src/utils/merchantColumnLayout.js
//
// The column layout the merchant made by hand: the order they dragged the
// columns into and the widths they dragged. AG Grid keeps every other column
// sized (gridDefaults, "Column sizing"); this module is what saves, restores
// and resets the merchant's part.
//
//   changedMerchantLayout(api, saved) — the layout to save for this grid:
//     `{ order?, widths? }`, null when the merchant has none (never moved or
//     resized anything, or reset), and undefined when it is what `saved`
//     already holds. Widths are read live and compared as a set: the saved
//     copy comes back from a PostgreSQL jsonb column, which does not keep key
//     order.
//   restoreMerchantLayout(api, layout) — puts saved widths back on a new grid.
//     The order comes back through useGridState's grid state instead, so AG
//     Grid holds the columns hidden until it is applied and nothing jumps. AG
//     Grid puts a column the saved order does not list (added or renamed since)
//     last; this moves it back beside the column it follows in the columnDefs.
//     From then on it does the same for a column the page declares on the live
//     grid (a new month, a column of the other mode), which `maintainColumnOrder`
//     also files last (ag-grid-community `applyPrevColumnsOrder`).
//   onMerchantLayoutReset(api, callback) — called after Reset columns dropped
//     the layout, which moves no width or order of its own and so reaches no
//     state event.
//   getColumnMenuItems — the grid option for the header's right-click menu:
//     Reset columns. `mainMenuItems` is a column option in AG Grid 36.2; the
//     grid-level hook is this one (colDef.d.ts `columnMenuItems`,
//     gridOptions.d.ts `getColumnMenuItems`).
//
// A width is the merchant's once a header-edge drag or a double-click on the
// edge changed it — the two arrive on the column as `widthChanged` with source
// `uiColumnResized`; the grid-level event of a double-click says
// `autosizeColumns`, like AG Grid's own sizing. That is also when AG Grid
// stops auto-sizing the column (`Column.setActualWidth`), and a width restored
// through applyColumnState stops it too (`applyFieldState`), so the two
// records agree. An order is the merchant's once they finished a move in the
// header or the column picker.
//
// Kept per grid instance: a rebuilt grid starts with nothing, and useGridState
// restores into it.

// The i18next instance i18n/i18n.jsx sets up at app start: the menu is built
// when it opens, outside React, so it reads the language current at that time.
import i18next from 'i18next';

const MERCHANT_MOVES = new Set(['uiColumnMoved', 'toolPanelUi', 'toolPanelDragAndDrop']);

const layouts = new WeakMap();

function layoutOf(api) {
  let layout = layouts.get(api);
  if (layout) return layout;
  layout = { sized: new Set(), ordered: false, onReset: null };
  layouts.set(api, layout);
  const watched = new WeakSet();
  const watchColumns = () => {
    for (const column of api.getAllGridColumns()) {
      if (watched.has(column)) continue;
      watched.add(column);
      column.addEventListener('widthChanged', ({ source }) => {
        if (source === 'uiColumnResized') layout.sized.add(column.getColId());
      });
    }
  };
  const colIdsOf = (columns) => columns.map((column) => column.getColId());
  let declared = new Set(colIdsOf(api.getColumns()));
  const placeNewColumns = () => {
    const defOrder = colIdsOf(api.getColumns());
    const kept = colIdsOf(api.getAllGridColumns()).filter((colId) => declared.has(colId));
    declared = new Set(defOrder);
    const placed = withUnlistedColumnsPlaced(kept, defOrder);
    if (placed) api.applyColumnState({ state: placed.map((colId) => ({ colId })), applyOrder: true });
  };
  watchColumns();
  api.addEventListener('newColumnsLoaded', () => {
    watchColumns();
    placeNewColumns();
  });
  api.addEventListener('columnMoved', ({ finished, source }) => {
    if (finished && MERCHANT_MOVES.has(source)) layout.ordered = true;
  });
  api.addEventListener('columnsReset', () => {
    layout.sized.clear();
    layout.ordered = false;
    layout.onReset?.();
  });
  return layout;
}

export function changedMerchantLayout(api, saved) {
  const { sized, ordered } = layoutOf(api);
  let layout = null;
  if (ordered || sized.size) {
    layout = {};
    if (ordered) layout.order = api.getAllGridColumns().map((column) => column.getColId());
    if (sized.size) {
      layout.widths = {};
      for (const colId of sized) {
        const column = api.getColumn(colId);
        if (column) layout.widths[colId] = column.getActualWidth();
      }
    }
  }
  return sameLayout(layout, saved) ? undefined : layout;
}

function sameLayout(layout, saved) {
  if (!layout || !saved) return !layout && !saved;
  const order = layout.order ?? [];
  const savedOrder = saved.order ?? [];
  if (order.length !== savedOrder.length || order.some((colId, i) => colId !== savedOrder[i])) return false;
  const widths = Object.entries(layout.widths ?? {});
  const savedWidths = saved.widths ?? {};
  return widths.length === Object.keys(savedWidths).length
    && widths.every(([colId, width]) => savedWidths[colId] === width);
}

export function restoreMerchantLayout(api, layout) {
  const tracked = layoutOf(api);
  if (!layout) return;
  const widths = layout.widths ?? {};
  const state = Object.entries(widths).map(([colId, width]) => ({ colId, width, flex: null }));
  if (state.length) api.applyColumnState({ state });
  if (layout.order) {
    const placed = withUnlistedColumnsPlaced(layout.order, api.getColumns().map((column) => column.getColId()));
    if (placed) api.applyColumnState({ state: placed.map((colId) => ({ colId })), applyOrder: true });
  }
  tracked.ordered = Boolean(layout.order);
  tracked.sized = new Set(Object.keys(widths));
}

export function onMerchantLayoutReset(api, callback) {
  layoutOf(api).onReset = callback;
}

// The saved order with every columnDefs column it does not list placed right
// after the column it follows in the columnDefs (or first, when it is the
// first), or null when the saved order lists them all.
export function withUnlistedColumnsPlaced(savedOrder, defOrder) {
  const listed = new Set(savedOrder);
  const unlisted = defOrder.filter((colId) => !listed.has(colId));
  if (!unlisted.length) return null;
  const order = savedOrder.filter((colId) => defOrder.includes(colId));
  for (const colId of unlisted) {
    const before = defOrder.slice(0, defOrder.indexOf(colId)).reverse().find((id) => order.includes(id));
    order.splice(before === undefined ? 0 : order.indexOf(before) + 1, 0, colId);
  }
  return order;
}

// Back to the columnDefs' order and widths, and to AG Grid sizing every
// column. resetColumnState is AG Grid's one way to hand a column the merchant
// sized back to auto-sizing (`resetWidthOwnership`), but it also resets which
// columns are hidden and the sort; those are put back as they were, because
// the merchant asked for the layout only.
function resetColumnLayout(api) {
  const kept = api.getColumnState().map(({ colId, hide, sort, sortIndex }) => ({ colId, hide, sort, sortIndex }));
  api.resetColumnState();
  api.applyColumnState({ state: kept });
}

// The column header's menu only: the Columns tool panel and the column
// chooser keep AG Grid's own items.
export const getColumnMenuItems = ({ source, defaultItems }) => (source === 'columnMenu'
  ? [{ name: i18next.t('grid.resetColumns'), action: ({ api }) => resetColumnLayout(api) }]
  : defaultItems);
